// Kyte seat-selection pipeline. Kyte's ShopAncillaries seatmap differs from
// Amadeus: prices are minor units (decimals:2 → divide by 100), availability
// is a single boolean per seat (no per-traveler array) so every traveler gets
// the same availability/price synthesized from seatCategories. Emits the
// canonical NormalizedDeckSeatMap / SeatMapResponse shape (providers/types.js)
// so the seat-selection UI stays provider-agnostic.

import { currency_symbol } from '@resources';
import { get_object_field } from '@/utilities/commons';
import { apply_markup_to_number } from '@liveflight/utilities/price';
import { traveler_type } from '@liveflight/utilities/travelers';
import {
  build_legend_hints,
  can_all_passengers_select_one_seat,
  deck_covers_all_passengers,
} from '@liveflight/utilities/seat-map';
import {
  minor_to_decimal,
  make_price,
  build_passenger_summary,
  build_deck_summary,
} from '@liveflight/providers/base/helpers';
import {
  RYANAIR_CARRIER_CODES,
  WIZZAIR_CARRIER_CODES,
  EASYJET_CARRIER_CODES,
} from '@liveflight/resources/carriers';
import { collect_traveler_ids } from './helpers';

const position_label = {
  window: 'Posto finestrino',
  aisle: 'Posto corridoio',
  middle: 'Posto centrale',
};

const build_display_characteristics = (seat) => {
  const out = [];
  const { position, isEmergencyExit, isFacingBulkhead } = seat;
  if (position && position_label[position]) {
    out.push({ code: position, label: position_label[position] });
  }
  if (isEmergencyExit) {
    out.push({ code: 'emergencyExit', label: 'Fila uscita di emergenza' });
  }
  if (isFacingBulkhead) {
    out.push({ code: 'bulkhead', label: 'Fronte paratia' });
  }
  return out;
};

// Kyte ships TWO restriction fields: `restrictions` (array, authoritative)
// and `isRestrictedFor` (single-string headline). Real payloads include rows
// where `isRestrictedFor:'none'` while `restrictions:['infant']`; the array
// wins. Emergency-exit rows are excluded by regulation regardless.
const is_suitable_for_infant = (seat) => {
  if (seat.isEmergencyExit) return false;
  const restrictions = Array.isArray(seat.restrictions)
    ? seat.restrictions
    : [];
  return !restrictions.some((r) => String(r).toLowerCase() === 'infant');
};

// Kyte's `restrictions` array already speaks the traveler_type vocabulary
// (infant/child/teen); pivot it into the canonical `restrictedFor` map that
// is_restricted_for_traveler reads. Non-traveler tokens (e.g. fragile) key
// harmlessly — no traveler_type ever matches them.
const build_seat_restrictions = (seat) => {
  const restrictions = Array.isArray(seat.restrictions)
    ? seat.restrictions
    : [];
  return restrictions.reduce(
    (restrictedFor, restriction) => ({
      ...restrictedFor,
      [traveler_type({ type: restriction })]: true,
    }),
    {}
  );
};

// Fresh price per traveler entry so an in-place mutation downstream can't
// bleed across travelers via shared reference. make_price returns
// total === grandTotal; override grandTotal with the per-tier marked-up value.
const build_traveler_pricing_entries = ({
  travelerIds,
  available,
  blocked,
  totalString,
  grandTotal,
  currencyCode,
}) => {
  let status = 'BLOCKED';
  let availabilityLabel = 'blocked';
  if (!blocked && available) {
    status = 'AVAILABLE';
    availabilityLabel = 'available';
  } else if (!blocked && !available) {
    status = 'OCCUPIED';
    availabilityLabel = 'occupied';
  }
  const list = travelerIds.map((travelerId) => ({
    travelerId,
    seatAvailabilityStatus: status,
    blackListed: !!blocked,
    price: { ...make_price(totalString, currencyCode), grandTotal },
  }));
  const byTraveler = {};
  travelerIds.forEach((travelerId) => {
    byTraveler[travelerId] = {
      availability: availabilityLabel,
      blackListed: !!blocked,
      price: { ...make_price(totalString, currencyCode), grandTotal },
    };
  });
  return { travelerPricing: list, pricingByTraveler: byTraveler };
};

// ─── Single deck: Kyte cabin → NormalizedDeckSeatMap ─────────────────────

const build_deck_seatmap = (flight_offer, cabin, seat_categories, currency) => {
  if (!cabin) return null;
  const { cabinConfiguration = [], rows = {} } = cabin;
  const width = cabinConfiguration.length;
  if (!width) return null;

  const travelerIds = collect_traveler_ids(flight_offer);
  const { code: currencyCode = 'EUR', decimals = 2 } = currency || {};
  const currencyGlyph = currency_symbol(currencyCode);

  const aircraftMap = {};
  const columns = {};
  const rowsMap = {};
  const priceMap = {};
  const availableSeatsRemoved = {};
  const availableSeatsCount = {};
  const passengerMinPrice = {};
  const passengerMaxPrice = {};
  const passengerHasInfantSeat = {};

  let hasSeatsSuitableForInfant = false;
  let deckTotalSeats = 0;
  let deckAvailableSeats = 0;
  let deckHasFreeSeats = false;
  let deckHasChargeableSeats = false;
  let deckHasOccupiedSeats = false;
  let deckHasBlockedSeats = false;
  let deckHasExitRows = false;
  let deckMinPrice = null;
  let deckMaxPrice = null;

  // cabinConfiguration counts its null hallway slots as space units, so the
  // derived width matches deck.vue's grid-template-columns directly.
  for (let col = 0; col < width; col += 1) {
    const letter = cabinConfiguration[col];
    columns[col] = {
      name: letter || '',
      isHallway: letter === null || letter === undefined,
    };
  }

  // Place seats by parsing the trailing character of `seat.number`
  // ("10A" → "A") against this map, so the grid stays correct when the raw
  // rows array is ragged or its order diverges from cabinConfiguration.
  const columnIndexByLetter = {};
  cabinConfiguration.forEach((letter, idx) => {
    if (letter) columnIndexByLetter[letter] = idx;
  });

  const rowKeys = Object.keys(rows).sort((a, b) => Number(a) - Number(b));

  rowKeys.forEach((rowLabel, rowIdx) => {
    const rowSeats = rows[rowLabel] || [];
    aircraftMap[rowIdx] = { blocks: {} };
    rowsMap[rowIdx] = Number(rowLabel);

    // Start every cell empty (including hallway columns); real seats
    // overwrite below.
    for (let colIdx = 0; colIdx < width; colIdx += 1) {
      aircraftMap[rowIdx].blocks[colIdx] = { type: 'empty' };
    }

    rowSeats.forEach((seat) => {
      if (!seat || !seat.number) {
        return;
      }
      const columnLetter = seat.number.slice(-1);
      const colIdx = columnIndexByLetter[columnLetter];
      if (colIdx === undefined) {
        return;
      }

      deckTotalSeats += 1;
      if (seat.available) {
        deckAvailableSeats += 1;
      } else {
        deckHasOccupiedSeats = true;
      }
      if (seat.isEmergencyExit) {
        deckHasExitRows = true;
      }

      // Missing/priceless category ⇒ BLOCKED. Falling back to a free seat
      // would mislead the user into picking something the BE has no price
      // for; book_ancillaries would then reject or under-bill.
      const categoryEntry = seat_categories[seat.category];
      const hasKnownPrice =
        categoryEntry &&
        categoryEntry.price !== undefined &&
        categoryEntry.price !== null;
      // A negative tier price is never billable, so treat it like a missing
      // price: block the seat and surface it at 0 rather than as a discount.
      const priceIsNegative = hasKnownPrice && Number(categoryEntry.price) < 0;
      const blocked = seat.available && (!hasKnownPrice || priceIsNegative);
      const category =
        hasKnownPrice && !priceIsNegative ? categoryEntry : { price: 0 };
      const totalString = minor_to_decimal(category.price, decimals);
      const totalMajor = Number(totalString);

      // Apply markup once per price tier (same pattern as Amadeus).
      let grandTotal = priceMap[totalString];
      if (!grandTotal) {
        const { travelerPricings } = flight_offer || {};
        grandTotal = travelerPricings
          ? String(apply_markup_to_number(flight_offer, totalMajor))
          : totalString;
        priceMap[totalString] = grandTotal;
      }

      const isSuitableInfant = is_suitable_for_infant(seat);
      const displayCharacteristics = build_display_characteristics(seat);

      const { travelerPricing, pricingByTraveler } =
        build_traveler_pricing_entries({
          travelerIds,
          available: seat.available,
          blocked,
          totalString,
          grandTotal,
          currencyCode,
        });

      if (blocked) {
        deckHasBlockedSeats = true;
        travelerIds.forEach((travelerId) => {
          availableSeatsRemoved[travelerId] =
            (availableSeatsRemoved[travelerId] || 0) + 1;
        });
      } else if (seat.available) {
        if (totalMajor === 0) deckHasFreeSeats = true;
        else deckHasChargeableSeats = true;
        // Deck + per-pax min/max store the RAW total; markup is applied at
        // render. The marked-up `grandTotal` lives only on the per-seat block.
        if (deckMinPrice === null || totalMajor < deckMinPrice) {
          deckMinPrice = totalMajor;
        }
        if (deckMaxPrice === null || totalMajor > deckMaxPrice) {
          deckMaxPrice = totalMajor;
        }

        travelerIds.forEach((travelerId) => {
          availableSeatsCount[travelerId] =
            (availableSeatsCount[travelerId] || 0) + 1;
          if (isSuitableInfant) passengerHasInfantSeat[travelerId] = true;

          const currentMin = passengerMinPrice[travelerId];
          if (!currentMin || totalMajor < Number(currentMin.total)) {
            passengerMinPrice[travelerId] = make_price(
              totalString,
              currencyCode
            );
          }
          const currentMax = passengerMaxPrice[travelerId];
          if (!currentMax || totalMajor > Number(currentMax.total)) {
            passengerMaxPrice[travelerId] = make_price(
              totalString,
              currencyCode
            );
          }
        });

        if (isSuitableInfant) hasSeatsSuitableForInfant = true;
      }

      aircraftMap[rowIdx].blocks[colIdx] = {
        ...seat,
        available: seat.available && !blocked,
        type: 'seat',
        number: seat.number,
        row: rowLabel,
        column: columnLetter,
        isPartiallyAvailable: false,
        isSuitableForInfant: isSuitableInfant,
        isPremium: false,
        displayCharacteristics,
        restrictedFor: build_seat_restrictions(seat),
        pricingByTraveler,
        travelerPricing,
      };
    });
  });

  // Emergency-exit strip — a row with any isEmergencyExit seat needs an exit
  // strip rendered in FRONT of it (deck-row.vue reads aircraftMap[row].exits).
  // The exitRowsX mirror in deckConfiguration is populated below so the same
  // renderer handles Kyte and Amadeus.
  rowKeys.forEach((rowLabel, rowIdx) => {
    const rowSeats = rows[rowLabel] || [];
    if (!rowSeats.some((s) => s && s.isEmergencyExit)) return;
    const exits = {
      0: { type: 'exit', direction: 'left' },
      [width - 1]: { type: 'exit', direction: 'right' },
    };
    for (let col = 1; col < width - 1; col += 1) {
      exits[col] = { type: 'empty' };
    }
    aircraftMap[rowIdx].exits = exits;
  });

  // Neighbor linking: reset only on hallway columns. An empty placeholder
  // cell is not a row boundary, so seats on either side of it stay linked.
  Object.keys(aircraftMap).forEach((rowIdxKey) => {
    const { blocks } = aircraftMap[rowIdxKey];
    let prevSeat = null;
    for (let col = 0; col < width; col += 1) {
      if (columns[col] && columns[col].isHallway) {
        prevSeat = null;
        continue;
      }
      const block = blocks[col];
      if (!block || block.type !== 'seat') continue;
      if (prevSeat) {
        block.left = {
          number: prevSeat.number,
          isBlocked: !prevSeat.available,
        };
        prevSeat.right = { number: block.number, isBlocked: !block.available };
      }
      prevSeat = block;
    }
  });

  const passengerSummary = build_passenger_summary({
    travelerIds,
    availableSeatsCount,
    availableSeatsRemoved,
    passengerHasInfantSeat,
    passengerMinPrice,
    passengerMaxPrice,
  });

  const deckSummary = build_deck_summary({
    totalSeats: deckTotalSeats,
    availableSeats: deckAvailableSeats,
    hasFreeSeats: deckHasFreeSeats,
    hasChargeableSeats: deckHasChargeableSeats,
    minPrice: deckMinPrice,
    maxPrice: deckMaxPrice,
    currency: currencyCode,
    currencySymbol: currencyGlyph,
  });

  return {
    hasSeatsSuitableForInfant,
    aircraftMap,
    availableSeatsRemoved,
    availableSeatsCount,
    passengerSummary,
    deckSummary,
    columns,
    rows: rowsMap,
    currencyCode: currencyCode || '',
    currencySymbol: currencyGlyph || '',
    legendHints: build_legend_hints({
      hasFreeSeats: deckHasFreeSeats,
      hasChargeableSeats: deckHasChargeableSeats,
      hasOccupiedSeats: deckHasOccupiedSeats,
      hasBlockedSeats: deckHasBlockedSeats,
      hasInfantSuitableSeats: hasSeatsSuitableForInfant,
      hasExitRows: deckHasExitRows,
    }),
  };
};

// One segment → one mapped entry. Segments without a BE-supplied map surface
// as disabled rather than silently mirroring another segment's plane.
const build_mapped_decks = (flight_offer, seatMap, currency, options = {}) => {
  if (!flight_offer || !seatMap) return {};
  const { areInfantsPresent = false } = options;
  const mapped = {};

  const allSegments = (flight_offer.itineraries || []).flatMap(
    (itin) => itin.segments || []
  );
  if (!allSegments.length) return {};

  allSegments.forEach((segment) => {
    const segmentFixture = seatMap[segment.id];
    if (!segmentFixture) {
      mapped[segment.id] = {
        decks: { 0: { disabled: true } },
        segment: {
          ...segment,
          segmentId: segment.id,
          decks: [{ disabled: true }],
          disabled: true,
        },
      };
      return;
    }
    const { cabins = [], seatCategories = {}, aircraft } = segmentFixture;

    const segmentDecks = {};
    if (!cabins.length) {
      segmentDecks[0] = { disabled: true };
    } else {
      cabins.forEach((cabin, cabinIdx) => {
        const deck = build_deck_seatmap(
          flight_offer,
          cabin,
          seatCategories,
          currency
        );
        if (!deck) {
          segmentDecks[cabinIdx] = { disabled: true };
          return;
        }
        const noInfantSeats =
          areInfantsPresent && !deck.hasSeatsSuitableForInfant;
        segmentDecks[cabinIdx] = {
          ...deck,
          disabled:
            !deck_covers_all_passengers(flight_offer, deck) || noInfantSeats,
        };
      });
    }

    const { carrier = {} } = segment;
    // Each deck entry on segment.decks[] MUST carry a deckConfiguration with
    // `width` — deck.vue reads it for grid-template-columns. Mirror Kyte's
    // cabinConfiguration (including null hallway slots) so seat and hallway
    // cells each get a grid column.
    const segmentDeckList = cabins.length
      ? cabins.map((cabin) => {
          const cabinCfg = cabin.cabinConfiguration || [];
          const cabinRows = cabin.rows || {};
          const rowIndexKeys = Object.keys(cabinRows).sort(
            (a, b) => Number(a) - Number(b)
          );
          const exitRowsX = [];
          rowIndexKeys.forEach((rowLabel, rowIdx) => {
            const seats = cabinRows[rowLabel] || [];
            if (seats.some((s) => s && s.isEmergencyExit)) {
              exitRowsX.push(rowIdx);
            }
          });
          return {
            deckConfiguration: {
              width: cabinCfg.length,
              exitRowsX,
              length: rowIndexKeys.length,
            },
          };
        })
      : [{ disabled: true }];

    mapped[segment.id] = {
      decks: segmentDecks,
      segment: {
        ...segment,
        segmentId: segment.id,
        carrierCode: carrier.code,
        aircraft,
        decks: segmentDeckList,
        disabled: Object.values(segmentDecks).every((d) => d.disabled),
      },
    };
  });

  return mapped;
};

// Envelope mirrors Amadeus raw data shape so the provider-neutral
// can_all_passengers_select_one_seat helper works (see providers/types.js).
const build_envelope_segments = (flight_offer, seatMap) => {
  if (!seatMap) return [];
  const keys = Object.keys(seatMap);
  if (!keys.length) return [];
  const travelerIds = collect_traveler_ids(flight_offer);

  return (flight_offer.itineraries || [])
    .flatMap((itin) => itin.segments || [])
    .map((segment) => {
      const source = seatMap[segment.id];
      if (!source) {
        return {
          segmentId: segment.id,
          carrierCode: (segment.carrier || {}).code,
          availableSeatsCounters: travelerIds.map((travelerId) => ({
            travelerId,
            value: 0,
          })),
          decks: [],
        };
      }
      const { cabins = [], seatCategories = {} } = source;
      // Sum availability across EVERY cabin so multi-deck aircraft don't
      // hard-fail can_all_passengers_select_one_seat.
      let available = 0;
      cabins.forEach((cabin) => {
        const { rows = {} } = cabin || {};
        Object.keys(rows).forEach((rowKey) => {
          (rows[rowKey] || []).forEach((seat) => {
            if (seat && seat.available) available += 1;
          });
        });
      });
      const { carrier = {} } = segment;
      return {
        segmentId: segment.id,
        carrierCode: carrier.code,
        availableSeatsCounters: travelerIds.map((travelerId) => ({
          travelerId,
          value: available,
        })),
        decks: cabins.length
          ? [{ deckConfiguration: {}, seats: [], seatCategories }]
          : [],
      };
    });
};

// MUST match by `amenityType` (not code): description-derived KYTE_<initials>
// codes never equal a literal like 'SEAT_SELECTION', so a previous code-based
// gate silently no-op'd on every Ryanair offer. amenityType is
// provider-neutral so the predicate also works for Amadeus.
//
// Discriminator: when the fare INCLUDES a reserved seat (isChargeable=false)
// the booking can't move forward without picking one. When it merely OFFERS
// a seat for a fee (isChargeable=true), the pick stays optional (the airline
// auto-allocates if skipped).
//
// Today this enforcement is only desired for Ryanair (FR / RR / RK); other
// LCCs that ship the same included-seat pattern stay permissive.
const has_carriers_segment = (flight_offer, carriers) => {
  const { itineraries = [] } = flight_offer || {};
  return itineraries.some((itinerary) => {
    const { segments = [] } = itinerary || {};
    return segments.some((segment) => {
      const { carrier = {} } = segment || {};
      return carriers.includes(carrier.code || '');
    });
  });
};

export const seats_are_mandatory = (flight_offer) => {
  const fare = String(
    get_object_field(flight_offer, 'fare.brand', 'basic')
  ).toLowerCase();
  if (
    has_carriers_segment(flight_offer, RYANAIR_CARRIER_CODES) &&
    fare !== 'basic'
  ) {
    return true;
  }
  if (
    has_carriers_segment(flight_offer, WIZZAIR_CARRIER_CODES) &&
    fare !== 'basic'
  ) {
    return true;
  }
  if (
    has_carriers_segment(flight_offer, EASYJET_CARRIER_CODES) &&
    fare !== 'light'
  ) {
    return true;
  }
  return false;
};

/**
 * Turn the Kyte ShopAncillaries seatMap slice into the canonical
 * SeatMapResponse shape the seat-selection UI consumes. Reused by
 * fetch_ancillaries on the unified ShopAncillaries response.
 * @param {object} raw_seat_map
 * @param {object} flight_offer
 * @param {object} [currency]
 * @param {object} [options]
 * @returns {object}
 */
export const build_seatmap_envelope = (
  raw_seat_map,
  flight_offer,
  currency,
  options = {}
) => {
  const seatMap = raw_seat_map || {};
  if (!Object.keys(seatMap).length) {
    return {
      data: { seatmaps: { data: [], dictionaries: {} } },
      dictionaries: {},
      empty: true,
      isValid: false,
      canAllPassengersSelectOneSeat: false,
      mappedDecks: {},
    };
  }
  const safe_currency = currency || { code: 'EUR', decimals: 2 };
  const envelopeSegments = build_envelope_segments(flight_offer, seatMap);
  const mappedDecks = build_mapped_decks(
    flight_offer,
    seatMap,
    safe_currency,
    options
  );
  const hasAnyMapping = Object.keys(mappedDecks).length > 0;

  return {
    data: { seatmaps: { data: envelopeSegments, dictionaries: {} } },
    dictionaries: {},
    empty: !envelopeSegments.length,
    isValid: hasAnyMapping,
    canAllPassengersSelectOneSeat:
      can_all_passengers_select_one_seat(envelopeSegments),
    mappedDecks,
  };
};
